import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import './AdminSpecialistsList.css';

function AdminSpecialistsList() {
  const [specialists, setSpecialists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isSequenceMode, setIsSequenceMode] = useState(false);
  const [sequenceSpecialists, setSequenceSpecialists] = useState([]);
  const [isSavingSequence, setIsSavingSequence] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSpecialists();
  }, []);

  const fetchSpecialists = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const { data, error: fetchError } = await supabase
        .from('specialists')
        .select('*')
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      setSpecialists(data || []);
    } catch (err) {
      console.error('Error fetching specialists:', err);
      setError('Failed to load specialists. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (specialistId, specialistName) => {
    try {
      const { error: deleteError } = await supabase
        .from('specialists')
        .delete()
        .eq('id', specialistId);

      if (deleteError) {
        throw deleteError;
      }

      setSpecialists(specialists.filter(s => s.id !== specialistId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting specialist:', err);
      alert(err.message || 'Failed to delete specialist. Please try again.');
    }
  };

  const handleEdit = (specialistId) => {
    navigate(`/admin-dashboard/specialists/edit/${specialistId}`);
  };

  const handleSetSequence = () => {
    // Sort specialists by display_order, then by created_at
    const sorted = [...specialists].sort((a, b) => {
      if (a.display_order === null && b.display_order === null) {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      if (a.display_order === null) return 1;
      if (b.display_order === null) return -1;
      return a.display_order - b.display_order;
    });
    setSequenceSpecialists(sorted);
    setIsSequenceMode(true);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newSequence = [...sequenceSpecialists];
    [newSequence[index - 1], newSequence[index]] = [newSequence[index], newSequence[index - 1]];
    setSequenceSpecialists(newSequence);
  };

  const handleMoveDown = (index) => {
    if (index === sequenceSpecialists.length - 1) return;
    const newSequence = [...sequenceSpecialists];
    [newSequence[index], newSequence[index + 1]] = [newSequence[index + 1], newSequence[index]];
    setSequenceSpecialists(newSequence);
  };

  const handleSaveSequence = async () => {
    try {
      setIsSavingSequence(true);
      
      // Update display_order for all specialists
      const updates = sequenceSpecialists.map((specialist, index) => ({
        id: specialist.id,
        display_order: index + 1
      }));

      // Batch update all specialists
      for (const update of updates) {
        const { error: updateError } = await supabase
          .from('specialists')
          .update({ display_order: update.display_order })
          .eq('id', update.id);

        if (updateError) {
          throw updateError;
        }
      }

      // Refresh specialists list
      await fetchSpecialists();
      setIsSequenceMode(false);
      setShowSuccessPopup(true);
      // Auto-hide after 3 seconds
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (err) {
      console.error('Error saving sequence:', err);
      alert('Failed to save sequence. Please try again.');
    } finally {
      setIsSavingSequence(false);
    }
  };

  const handleCancelSequence = () => {
    setIsSequenceMode(false);
    setSequenceSpecialists([]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredSpecialists = specialists.filter(specialist =>
    specialist.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    specialist.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    specialist.specialization?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="admin-list-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading specialists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-list-container">
      <div className="list-header">
        <h1 className="list-title">Specialists</h1>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button 
            className="set-sequence-btn"
            onClick={handleSetSequence}
            disabled={specialists.length === 0}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M7 12h10M11 18h6"></path>
            </svg>
            Set Sequence
          </button>
          <button
            className="btn-add"
            onClick={() => navigate('/admin-dashboard/specialists/add')}
          >
            + Add New Specialist
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search specialists by name, title, or specialization..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredSpecialists.length === 0 ? (
        <div className="empty-state">
          <p>{searchTerm ? 'No specialists found matching your search.' : 'No specialists added yet.'}</p>
          <button
            className="btn-add"
            onClick={() => navigate('/admin-dashboard/specialists/add')}
          >
            Add Your First Specialist
          </button>
        </div>
      ) : (
        <div className="specialists-grid">
          {filteredSpecialists.map((specialist) => (
            <div key={specialist.id} className="specialist-card">
              <div className="specialist-image-container">
                <img
                  src={specialist.image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop'}
                  alt={specialist.name}
                  className="specialist-image"
                />
                {!specialist.is_active && (
                  <div className="inactive-badge">Inactive</div>
                )}
              </div>
              <div className="specialist-content">
                <h3 className="specialist-name">{specialist.name}</h3>
                {specialist.title && (
                  <p className="specialist-title">{specialist.title}</p>
                )}
                {specialist.specialization && (
                  <p className="specialist-specialization">
                    <strong>Specialization:</strong> {specialist.specialization}
                  </p>
                )}
                {specialist.experience_years && (
                  <p className="specialist-experience">
                    <strong>Experience:</strong> {specialist.experience_years} years
                  </p>
                )}
                {specialist.languages && Array.isArray(specialist.languages) && specialist.languages.length > 0 && (
                  <p className="specialist-languages">
                    <strong>Languages:</strong> {specialist.languages.join(', ')}
                  </p>
                )}
                {specialist.bio && (
                  <p className="specialist-bio">{specialist.bio.substring(0, 100)}...</p>
                )}
                <div className="specialist-actions">
                  <button
                    className="btn-edit"
                    onClick={() => handleEdit(specialist.id)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => setDeleteConfirm(specialist.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sequence Mode Modal */}
      {isSequenceMode && (
        <div className="sequence-modal-overlay" onClick={handleCancelSequence}>
          <div className="sequence-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sequence-modal-header">
              <h3>Set Specialist Sequence</h3>
              <p>Reorder specialists by clicking the up/down arrows. The order will be saved to the database.</p>
            </div>
            <div className="sequence-list">
              {sequenceSpecialists.map((specialist, index) => (
                <div key={specialist.id} className="sequence-item">
                  <div className="sequence-item-number">{index + 1}</div>
                  <div className="sequence-item-content">
                    <h4>{specialist.name}</h4>
                    <p className="sequence-item-meta">
                      {specialist.title || 'No title'} • {specialist.specialization || 'No specialization'}
                    </p>
                  </div>
                  <div className="sequence-item-actions">
                    <button
                      className="sequence-move-btn"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="18 15 12 9 6 15"></polyline>
                      </svg>
                    </button>
                    <button
                      className="sequence-move-btn"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === sequenceSpecialists.length - 1}
                      title="Move down"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="sequence-modal-actions">
              <button 
                className="cancel-btn"
                onClick={handleCancelSequence}
                disabled={isSavingSequence}
              >
                Cancel
              </button>
              <button 
                className="save-sequence-btn"
                onClick={handleSaveSequence}
                disabled={isSavingSequence}
              >
                {isSavingSequence ? 'Saving...' : 'Save Sequence'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="success-popup-overlay" onClick={() => setShowSuccessPopup(false)}>
          <div className="success-popup" onClick={(e) => e.stopPropagation()}>
            <div className="success-popup-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" className="success-circle"/>
                <path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="success-check"/>
              </svg>
            </div>
            <h3 className="success-popup-title">Sequence Saved!</h3>
            <p className="success-popup-message">Specialist order has been updated successfully.</p>
            <button 
              className="success-popup-close"
              onClick={() => setShowSuccessPopup(false)}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this specialist? This action cannot be undone.</p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-delete"
                onClick={() => {
                  const specialist = specialists.find(s => s.id === deleteConfirm);
                  handleDelete(deleteConfirm, specialist?.name);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSpecialistsList;
